import type React from "react";
import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useBlockWindowQuery } from "../../components/table/useBlockWindowQuery";
import { css } from "../../../styled-system/css";
import { useServerTable, type AppColumns } from "../../components/table/tableFeatures";
import { type ManagedTableState, type ManagedTableStateSetters } from "../../components/table/usePersistedTableState";
import { Flex, HStack, VStack, Wrap } from "../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../components/fields/base/TextField";
import { RT8Pagination } from "../../components/table/Pagination";
import { Table } from "../../components/table/Table";
import { resetTable, selectionStateToSelectedIds } from "../../components/table/utils";
import { RT8FilterWidget } from "../merchandising/components/tables/FilterWidget";
import { Button } from "../../components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { TableErrorWrapper } from "../../components/table/TableErrorWrapper";
import { type OrgQueryV4Row, useGetV4OrgReportByOrg } from "../perf_reports/api/organization/v4OrgReportByOrg";
import OrgPerformanceExpander from "../merchandising/components/OrgPerformanceExpander";
import BulkTagsForm from "../../components/core/blocks/BulkTagsForm";
import { BulkActionButton } from "../../components/table/BulkActionButton";
import { Switch } from "../../components/fields/base/Switch";
import { useBulkUpdateOrgProperties } from "../perf_reports/api/organization/useBulkUpdateOrgProperties";
import { useGetV4OrgReportOptions } from "../perf_reports/api/organization/v4OrgReportOptions";
import { useAbility } from "../../config/casl/casl";
import { subject } from "@casl/ability";
import { useModuleEnabled } from "../../redux/selectors/modules";
import { useTagFilterOptions } from "../tags/api/useTagFilterOptions";
import { EntityTagsFormModal } from "../tags/components/EntityTagsFormModal";
import NiceModal from "@ebay/nice-modal-react";
import { DataViewPicker } from "../dataViews/components/DataViewPicker";

export const AGENCY_DASHBOARD_STAT_CHANNELS = ["AMAZON", "WALMART"];

const AgencyOrgReportTable = ({
  columns,
  state,
  initialState,
  setters,
  selectedMarketplace,
  showHiddenOrgs,
  setShowHiddenOrgs,
  allActive = false,
}: {
  columns: AppColumns<OrgQueryV4Row>;
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  selectedMarketplace: string;
  showHiddenOrgs: boolean;
  setShowHiddenOrgs: React.Dispatch<React.SetStateAction<boolean>>;
  allActive?: boolean;
}) => {
  const { t } = useTranslation(["organization", "common"]);
  const [filledTags, setFilledTags] = useState<string[]>([]);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const ability = useAbility();

  const filters = useMemo(() => {
    const hiddenOrgsFilter =
      showHiddenOrgs ? [] : [{ id: "org_is_visible", value: { op: "=", value: !showHiddenOrgs } }];
    const orgChannelFilter = [
      {
        id: "org_type",
        value: {
          op: "anyof",
          value: AGENCY_DASHBOARD_STAT_CHANNELS,
        },
      },
    ];

    return [...orgChannelFilter, ...state.columnFilters, ...hiddenOrgsFilter];
  }, [state.columnFilters, showHiddenOrgs]);

  const byOrgOptionsGetter = useGetV4OrgReportByOrg(selectedMarketplace);
  const orgByOrgReportQuery = useBlockWindowQuery(byOrgOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: filters,
    search: debounceSearch,
    allActive,
  });

  const orgReportOptionsQuery = useQuery({ ...useGetV4OrgReportOptions(), enabled: !tagsEnhancedEnabled });
  const tagEnhancedOptions = useTagFilterOptions("ORGANIZATION", { enabled: tagsEnhancedEnabled });

  const tagOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.common["All Tags"]) },
      ...(orgReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [orgReportOptionsQuery.data?.data.tags, t],
  );

  const table = useServerTable({
    data: orgByOrgReportQuery.data?.data,
    columns,
    getRowCanExpand: () => true,
    getRowId: (row) => row.org_id.toString(),
    enableRowSelection: (row) => {
      return ability.can("update", subject("Org", { org_id: row.original.org_id }));
    },
    rowCount: orgByOrgReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    alternateSortingEnabled: true,
    meta: {
      showSkeleton:
        orgByOrgReportQuery.isFetching && (!orgByOrgReportQuery.data || orgByOrgReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        channel: [
          {
            value: "AMAZON",
            label: t(($) => $.organization.channels["Amazon"]),
          },
          {
            value: "WALMART",
            label: t(($) => $.organization.channels["Walmart"]),
          },
          {
            value: "SHOPIFY",
            label: t(($) => $.organization.channels["Shopify"]),
          },
        ],
      },
    },
  });

  const bulkUpdateOrgPropertiesMutation = useBulkUpdateOrgProperties();
  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  const addTagsLabel = t(($) => $.organization["Add Organization Tags"]);
  const removeTagsLabel = t(($) => $.organization["Remove Organization Tags"]);
  const taggedItemDescription = t(($) => $.organization.tagsItemDescription);
  const tagWord =
    filledTags.length > 1 ?
      t(($) => $.organization.confirm["these tags"])
    : t(($) => $.organization.confirm["this tag"]);

  return (
    <div>
      <Wrap justifyContent={"end"} alignItems={"center"} width={"100%"} padding={"small"} fontSize={"large"}>
        <BulkActionButton
          selected={selected}
          options={[
            ...(tagsEnhancedEnabled ?
              [
                {
                  label: addTagsLabel,
                  action: () => {
                    void NiceModal.show(EntityTagsFormModal, {
                      entityType: "ORGANIZATION" as const,
                      title: addTagsLabel,
                      onSubmit: async (tagIds) => {
                        await bulkUpdateOrgPropertiesMutation.mutateAsync({
                          org_ids: selected.map((id) => parseInt(id)),
                          updates: [{ field: "tags", action: "add", value: tagIds }],
                        });
                      },
                    });
                  },
                },
                {
                  label: removeTagsLabel,
                  action: () => {
                    void NiceModal.show(EntityTagsFormModal, {
                      entityType: "ORGANIZATION" as const,
                      title: removeTagsLabel,
                      onSubmit: async (tagIds) => {
                        await bulkUpdateOrgPropertiesMutation.mutateAsync({
                          org_ids: selected.map((id) => parseInt(id)),
                          updates: [{ field: "tags", action: "remove", value: tagIds }],
                        });
                      },
                    });
                  },
                },
              ]
            : [
                {
                  label: addTagsLabel,
                  action: () => {
                    bulkUpdateOrgPropertiesMutation.mutate(
                      {
                        org_ids: selected.map((id) => parseInt(id)),
                        updates: [
                          {
                            field: "tags" as const,
                            action: "add" as const,
                            value: filledTags,
                          },
                        ],
                      },
                      { onSuccess: () => setFilledTags([]) },
                    );
                  },
                  actionFormFields: (
                    <BulkTagsForm
                      filledTags={filledTags}
                      setFilledTags={setFilledTags}
                      selected={selected}
                      suggestedTags={tagOptions.filter((tag) => tag.value !== "all").map((tag) => tag.value)}
                      taggedItemDescription={taggedItemDescription}
                      actionType={"add"}
                    />
                  ),
                  confirmationMessage: (
                    <p>
                      {t(
                        ($) =>
                          $.organization.confirm[
                            "Are you sure you want to add these tags to the selected organizations?"
                          ],
                        {
                          count: selected.length,
                          tagWord,
                        },
                      )}
                    </p>
                  ),
                  disableConfirmButton: !filledTags.length,
                },
                {
                  label: removeTagsLabel,
                  action: () => {
                    bulkUpdateOrgPropertiesMutation.mutate(
                      {
                        org_ids: selected.map((id) => parseInt(id)),
                        updates: [
                          {
                            field: "tags" as const,
                            action: "remove" as const,
                            value: filledTags,
                          },
                        ],
                      },
                      { onSuccess: () => setFilledTags([]) },
                    );
                  },
                  actionFormFields: (
                    <BulkTagsForm
                      filledTags={filledTags}
                      setFilledTags={setFilledTags}
                      selected={selected}
                      suggestedTags={tagOptions.filter((tag) => tag.value !== "all").map((tag) => tag.value)}
                      taggedItemDescription={taggedItemDescription}
                      actionType={"remove"}
                    />
                  ),
                  disableConfirmButton: !filledTags.length,
                  confirmationMessage: (
                    <p>
                      {t(
                        ($) =>
                          $.organization.confirm[
                            "Are you sure you want to remove these tags from the selected organizations?"
                          ],
                        {
                          count: selected.length,
                          tagWord,
                        },
                      )}
                    </p>
                  ),
                },
              ]),
            {
              label: t(($) => $.organization["Hide Brands"], { count: selected.length }),
              action: () => {
                bulkUpdateOrgPropertiesMutation.mutate({
                  org_ids: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "mp_visibility",
                      action: "set",
                      value: {
                        visibility: "hide",
                        marketplace: selectedMarketplace,
                      },
                    },
                  ],
                });
              },
              confirmationMessage: (
                <>
                  <p>
                    {t(($) => $.organization.confirm["Are you sure you want to hide these brands?"], {
                      count: selected.length,
                    })}
                  </p>
                  <p>
                    {t(
                      ($) =>
                        $.organization.confirm['You can un-hide them at anytime by clicking the "Show Hidden Brands"'],
                    )}
                  </p>
                </>
              ),
            },
            {
              label: t(($) => $.organization["Show Brands"], { count: selected.length }),
              action: () => {
                bulkUpdateOrgPropertiesMutation.mutate({
                  org_ids: selected.map((id) => parseInt(id)),
                  updates: [
                    {
                      field: "mp_visibility",
                      action: "set",
                      value: {
                        visibility: "show",
                        marketplace: selectedMarketplace,
                      },
                    },
                  ],
                });
              },
              confirmationMessage: (
                <p>
                  {t(($) => $.organization.confirm["Are you sure you want to show these brands?"], {
                    count: selected.length,
                  })}
                </p>
              ),
            },
          ]}
        />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table, "agencyDashboardOrgReport");
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey="agencyDashboardOrgReport" datasetKey="organization" table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <Flex
          className={css({
            gap: "small",
            alignItems: "center",
            marginEnd: "xxlarge",
            marginBottom: "large",
            fontSize: "small",
            fontWeight: 500,
          })}
        >
          <Switch onChange={() => setShowHiddenOrgs(!showHiddenOrgs)} isSelected={showHiddenOrgs} />
          {t(($) => $.organization["Show Hidden Brands"])}
        </Flex>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <TableErrorWrapper
        error={orgByOrgReportQuery.error}
        userMessage={t(($) => $.organization["Error loading stats"])}
      >
        <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={(row) => <OrgPerformanceExpander row={{ _original: row.original }} />}
        />
      </TableErrorWrapper>
    </div>
  );
};

export default AgencyOrgReportTable;
